import {
  Award,
  Bath,
  BedDouble,
  Boxes,
  BriefcaseBusiness,
  Building2,
  Calendar,
  CalendarClock,
  Car,
  CircleCheck,
  CircleDot,
  ClipboardList,
  Contact,
  Euro,
  Eye,
  FileText,
  Flower2,
  GraduationCap,
  HardHat,
  Hash,
  Home,
  IdCard,
  Landmark,
  Layers,
  LayoutGrid,
  Library,
  Mail,
  MapPin,
  Palette,
  Percent,
  Phone,
  Ruler,
  Section,
  Shapes,
  Sparkles,
  Square,
  Trees,
  Type,
  User,
  Wrench,
  Zap,
  type LucideIcon,
} from 'lucide-react'

import type { FieldKey } from '@brickup/shared/constants'

/**
 * Canonical field definitions used across every entity (Property, Project,
 * Address, SearchMandate, …). Every place that renders a field icon, label,
 * or unit must read from this map via `useField(key)`. Do **not** hand-pick
 * icons or invent translation keys for fields listed here.
 *
 * Only the icon lives here — `lucide-react` cannot be imported from
 * `shared/`. The label is `shared/locales/de/fields.ts` and the unit and
 * precision are `shared/constants/field-format.ts`; `useField(key)` merges the
 * three. If a field is missing, add it here and there — the key sets are
 * checked against each other, so an icon without a label fails the build.
 */
type FieldDef = { icon: LucideIcon }

export const FIELDS = {
  // ─── Areas ──────────────────────────────────────────────────────────
  livingArea: { icon: Ruler },
  usableArea: { icon: Square },
  landArea: { icon: Trees },
  gardenArea: { icon: Flower2 },
  balconyArea: { icon: Flower2 },
  wallArea: { icon: Square },

  // ─── Counts ─────────────────────────────────────────────────────────
  numberOfRooms: { icon: BedDouble },
  numberOfBathrooms: { icon: Bath },
  numberOfFloors: { icon: Layers },
  numberOfIndoorParkingSpaces: { icon: Car },
  numberOfOutdoorParkingSpaces: { icon: Car },
  floorNumber: { icon: Layers },

  // ─── Years ──────────────────────────────────────────────────────────
  constructionYear: { icon: Calendar },
  renovationYear: { icon: Wrench },
  createdAt: { icon: Calendar },
  updatedAt: { icon: Calendar },
  lastSeenAt: { icon: Eye },

  // ─── Energy ─────────────────────────────────────────────────────────
  energyLabel: { icon: Zap },
  energyConsumption: { icon: Zap },

  // ─── Prices ─────────────────────────────────────────────────────────
  marketPrice: { icon: Euro },
  potentialPrice: { icon: Sparkles },

  // ─── Flags ──────────────────────────────────────────────────────────
  leasehold: { icon: Section },
  isNew: { icon: Sparkles },

  // ─── Property type ──────────────────────────────────────────────────
  type: { icon: Home },
  subType: { icon: Home },

  // ─── Generic entity fields ──────────────────────────────────────────
  name: { icon: FileText },
  lastName: { icon: User },
  firstName: { icon: User },
  salutation: { icon: Contact },
  academicTitle: { icon: GraduationCap },
  customerNumber: { icon: Hash },
  userType: { icon: Building2 },
  phoneType: { icon: Phone },
  note: { icon: FileText },
  roles: { icon: IdCard },
  email: { icon: Mail },
  title: { icon: Type },
  status: { icon: CircleDot },
  configuratorStatus: { icon: Sparkles },
  notes: { icon: FileText },
  description: { icon: FileText },
  serviceDescription: { icon: FileText },
  customerHint: { icon: Eye },
  icon: { icon: Shapes },

  // ─── Address ────────────────────────────────────────────────────────
  address: { icon: MapPin },
  street: { icon: MapPin },
  houseNumber: { icon: MapPin },
  postalCode: { icon: MapPin },
  city: { icon: MapPin },
  district: { icon: MapPin },

  // ─── People ─────────────────────────────────────────────────────────
  contactPerson: { icon: User },
  projectManager: { icon: ClipboardList },
  siteManager: { icon: HardHat },
  customer: { icon: User },
  phone: { icon: Phone },
  gender: { icon: User },
  jobTitle: { icon: BriefcaseBusiness },
  calendarBookingUrl: { icon: Calendar },

  // ─── Rooms ──────────────────────────────────────────────────────────
  roomType: { icon: Home },
  productStandard: { icon: Award },
  productCategory: { icon: LayoutGrid },
  productGroup: { icon: Boxes },
  designLine: { icon: Palette },
  collection: { icon: Library },
  isActive: { icon: CircleCheck },
  floor: { icon: Layers },
  length: { icon: Ruler },
  width: { icon: Ruler },
  height: { icon: Ruler },
  perimeter: { icon: Ruler },
  area: { icon: Square },

  // ─── Cost entries ───────────────────────────────────────────────────
  flatRateCost: { icon: Euro },
  quantity: { icon: Hash },
  rowCount: { icon: Hash },
  unit: { icon: Ruler },

  // ─── Finances ───────────────────────────────────────────────────────
  netAmount: { icon: Euro },
  grossAmount: { icon: Euro },
  vatRate: { icon: Percent },
  percent: { icon: Percent },
  amount: { icon: Euro },
  invoiceNumber: { icon: Hash },
  invoiceDate: { icon: Calendar },
  dueDate: { icon: CalendarClock },
  paidAt: { icon: Calendar },
  approvedAt: { icon: Calendar },
  signedAt: { icon: Calendar },
  reachedAt: { icon: CircleCheck },
  paymentTermDays: { icon: CalendarClock },
  iban: { icon: Landmark },
  reference: { icon: FileText },
  contactName: { icon: Contact },
} as const satisfies Record<FieldKey, FieldDef>
